import React, { useState } from "react";
import { Modal } from "../common/Modal";
import { Opportunity, OpportunityStage, StaffMember } from "../../types";
import { Upload, Tag, X, Plus } from "lucide-react";
import { uploadRfpDocument } from "../../services/storageService";

const DEFAULT_SERVICES = [
  "Translation",
  "Localization",
  "Interpreting (VRI/OPI)",
  "On-Site Interpreting",
  "Desktop Publishing (DTP)",
  "Transcription",
  "Subtitling & Captioning",
  "Voiceover & Dubbing",
  "Machine Translation Post-Editing (MTPE)",
  "Multilingual SEO",
  "Transcreation & Copywriting",
  "Staffing & Dedicated Teams",
];

const STAGE_LABELS: Record<OpportunityStage, string> = {
  reviewing: "Intake & Reviewing",
  go: "Qualified (Go)",
  no_go: "Declined (No-Go)",
  drafting: "Drafting",
  in_review: "In Review",
  approved: "Approved",
  submitted: "Submitted",
  won: "Won",
  lost: "Lost",
  archived: "Archived / Ended Early",
};

interface OpportunityModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: Opportunity | null;
  staff: StaffMember[];
  availableServices?: string[];
  currentUser: { displayName: string; email: string };
  onSave: (
    data: Omit<Opportunity, "id" | "createdAt" | "updatedAt" | "stageHistory">,
  ) => Promise<void>;
}

const OpportunityForm: React.FC<OpportunityModalProps> = ({
  onClose,
  initialData,
  staff,
  availableServices = DEFAULT_SERVICES,
  currentUser,
  onSave,
}) => {
  const [client, setClient] = useState(initialData?.client || "");
  const [title, setTitle] = useState(initialData?.title || "");
  const [selectedServices, setSelectedServices] = useState<string[]>(
    initialData?.services || ["Translation"],
  );
  const [customServices, setCustomServices] = useState<string[]>([]);
  const [customService, setCustomService] = useState("");
  const [stage, setStage] = useState<OpportunityStage>(initialData?.stage || "reviewing");
  const [dueDate, setDueDate] = useState(
    () => initialData?.dueDate || new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
  );
  const [questionsDueDate, setQuestionsDueDate] = useState(initialData?.questionsDueDate || "");
  const [owner, setOwner] = useState(
    initialData?.owner || staff[0]?.displayName || currentUser.displayName,
  );
  const [rfpFile, setRfpFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pool of all available services (presets + custom added + initial)
  const allServices = Array.from(
    new Set([...availableServices, ...DEFAULT_SERVICES, ...customServices, ...selectedServices]),
  );

  const toggleService = (svc: string) => {
    setSelectedServices((prev) =>
      prev.includes(svc) ? prev.filter((s) => s !== svc) : [...prev, svc],
    );
  };

  const removeService = (svc: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedServices((prev) => prev.filter((s) => s !== svc));
  };

  const handleAddCustomService = (e: React.KeyboardEvent | React.MouseEvent) => {
    if ("key" in e && e.key !== "Enter") return;
    e.preventDefault();
    e.stopPropagation();

    const trimmed = customService.trim();
    if (!trimmed) return;

    if (!customServices.includes(trimmed)) {
      setCustomServices((prev) => [...prev, trimmed]);
    }
    if (!selectedServices.includes(trimmed)) {
      setSelectedServices((prev) => [...prev, trimmed]);
    }
    setCustomService("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!client.trim() || !title.trim() || !dueDate) {
      setError("Please provide client name, proposal title, and submission deadline.");
      return;
    }

    setSubmitting(true);
    setError(null);

    const selectedStaffMember = staff.find((s) => s.displayName === owner);
    const docPaths = initialData?.documentPaths ? [...initialData.documentPaths] : [];

    try {
      // If a source RFP file is selected, upload it to isolated storage
      if (rfpFile) {
        setUploadingFile(true);
        const tempOppId = initialData?.id || `opp-${Date.now()}`;
        const uploadResult = await uploadRfpDocument(tempOppId, rfpFile);
        docPaths.push(uploadResult.storagePath);
      }

      await onSave({
        client: client.trim(),
        title: title.trim(),
        services: selectedServices,
        dueDate,
        ...(questionsDueDate.trim() ? { questionsDueDate: questionsDueDate.trim() } : {}),
        stage,
        owner: owner || currentUser.displayName,
        ownerEmail: selectedStaffMember?.email || currentUser.email,
        documentPaths: docPaths,
        decision: initialData?.decision || null,
        noGoReason: initialData?.noGoReason || null,
        outcome: initialData?.outcome || null,
        qualifyChecklist: initialData?.qualifyChecklist || null,
      });

      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to record opportunity.");
    } finally {
      setSubmitting(false);
      setUploadingFile(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="rounded-lg bg-rose-50 p-3 text-xs font-semibold text-rose-700 dark:bg-rose-950/50 dark:text-rose-300">
          {error}
        </div>
      )}

      <div>
        <label
          htmlFor="opp-client"
          className="block text-xs font-semibold uppercase text-slate-600 dark:text-slate-400"
        >
          Client / Issuing Organization <span className="text-rose-500">*</span>
        </label>
        <input
          id="opp-client"
          type="text"
          required
          value={client}
          onChange={(e) => setClient(e.target.value)}
          placeholder="e.g., State Health & Human Services Agency"
          className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100"
        />
      </div>

      <div>
        <label
          htmlFor="opp-title"
          className="block text-xs font-semibold uppercase text-slate-600 dark:text-slate-400"
        >
          Proposal / Contract Title <span className="text-rose-500">*</span>
        </label>
        <input
          id="opp-title"
          type="text"
          required
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Statewide Multilingual Translation & Remote Interpreting Services"
          className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100"
        />
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label
            htmlFor="opp-due-date"
            className="block text-xs font-semibold uppercase text-slate-600 dark:text-slate-400"
          >
            Proposal Due Date <span className="text-rose-500">*</span>
          </label>
          <input
            id="opp-due-date"
            type="date"
            required
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100"
          />
        </div>

        <div>
          <label
            htmlFor="opp-questions-due-date"
            className="block text-xs font-semibold uppercase text-slate-600 dark:text-slate-400"
          >
            Questions Deadline (Optional)
          </label>
          <input
            id="opp-questions-due-date"
            type="date"
            value={questionsDueDate}
            onChange={(e) => setQuestionsDueDate(e.target.value)}
            className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100"
          />
        </div>

        <div>
          <label
            htmlFor="opp-stage"
            className="block text-xs font-semibold uppercase text-slate-600 dark:text-slate-400"
          >
            Pipeline Stage
          </label>
          <select
            id="opp-stage"
            value={stage}
            onChange={(e) => setStage(e.target.value as OpportunityStage)}
            className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100 font-medium"
          >
            {(Object.keys(STAGE_LABELS) as OpportunityStage[]).map((stg) => (
              <option key={stg} value={stg}>
                {STAGE_LABELS[stg]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label
          htmlFor="opp-owner"
          className="block text-xs font-semibold uppercase text-slate-600 dark:text-slate-400"
        >
          Proposal Owner (Staff HQ)
        </label>
        <select
          id="opp-owner"
          value={owner}
          onChange={(e) => setOwner(e.target.value)}
          className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100"
        >
          {staff.map((s) => (
            <option key={s.id} value={s.displayName}>
              {s.displayName} {s.department ? `(${s.department})` : ""}
            </option>
          ))}
          {!staff.some((s) => s.displayName === owner) && owner && (
            <option value={owner}>{owner}</option>
          )}
        </select>
      </div>

      {/* Services Selection and Custom Add */}
      <div>
        <div className="flex items-center justify-between">
          <label className="block text-xs font-semibold uppercase text-slate-600 dark:text-slate-400">
            Required Services ({selectedServices.length} selected)
          </label>
          <span className="text-[11px] text-slate-400">
            Click tags to select/deselect or add custom below
          </span>
        </div>

        {/* Selected Services Tags */}
        {selectedServices.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1.5 p-2 rounded-lg bg-teal-50/50 border border-teal-100 dark:bg-teal-950/20 dark:border-teal-900/40">
            {selectedServices.map((svc) => (
              <span
                key={svc}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#2ba5b5] px-2.5 py-1 text-xs font-semibold text-white shadow-2xs"
              >
                <Tag className="h-3 w-3" />
                <span>{svc}</span>
                <button
                  type="button"
                  onClick={(e) => removeService(svc, e)}
                  className="rounded-full p-0.5 hover:bg-white/20 transition-colors"
                  title={`Remove ${svc}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Available preset chips to toggle */}
        <div className="mt-2 flex flex-wrap gap-1.5 max-h-28 overflow-y-auto pr-1">
          {allServices
            .filter((svc) => !selectedServices.includes(svc))
            .map((svc) => (
              <button
                key={svc}
                type="button"
                onClick={() => toggleService(svc)}
                className="btn-animate inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-medium text-slate-600 hover:border-teal-300 hover:bg-teal-50/50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                <Plus className="h-3 w-3 text-slate-400" />
                <span>{svc}</span>
              </button>
            ))}
        </div>

        {/* Custom Service Input */}
        <div className="mt-2.5 flex items-center gap-2">
          <input
            type="text"
            value={customService}
            onChange={(e) => setCustomService(e.target.value)}
            onKeyDown={handleAddCustomService}
            placeholder="Add other service and press Enter (e.g., Braille Translation, ASL Video)..."
            className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-900 outline-hidden focus:border-[#2ba5b5] dark:border-slate-800 dark:bg-slate-950 dark:text-slate-100"
          />
          <button
            type="button"
            onClick={handleAddCustomService}
            className="btn-animate inline-flex items-center gap-1 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200"
          >
            <Plus className="h-3.5 w-3.5" />
            <span>Add Service</span>
          </button>
        </div>
      </div>

      {/* Source Document Attachment */}
      <div>
        <label className="block text-xs font-semibold uppercase text-slate-600 dark:text-slate-400">
          Attach RFP Document (PDF / DOCX)
        </label>
        <div className="mt-1.5 flex items-center gap-3">
          <label className="btn-animate inline-flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-2xs hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200">
            <Upload className="h-3.5 w-3.5 text-[#2ba5b5]" />
            <span>{rfpFile ? "Change Document" : "Upload RFP File"}</span>
            <input
              type="file"
              className="hidden"
              accept=".pdf,.docx,.doc"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) setRfpFile(f);
              }}
            />
          </label>
          {rfpFile && (
            <span className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-300 truncate">
              <span className="font-medium truncate">{rfpFile.name}</span>
              <button
                type="button"
                onClick={() => setRfpFile(null)}
                className="text-slate-400 hover:text-slate-600 ml-1"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t border-slate-100 dark:border-slate-800">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="btn-animate rounded-lg bg-[#2ba5b5] px-5 py-2 text-sm font-semibold text-white hover:bg-[#238997] disabled:opacity-50"
        >
          {uploadingFile
            ? "Uploading File..."
            : submitting
              ? "Saving..."
              : initialData
                ? "Update Opportunity"
                : "Save Opportunity"}
        </button>
      </div>
    </form>
  );
};

export const OpportunityModal: React.FC<OpportunityModalProps> = (props) => (
  <Modal
    isOpen={props.isOpen}
    onClose={props.onClose}
    maxWidth="2xl"
    title={props.initialData ? "Edit Opportunity" : "Add New RFP Opportunity"}
    description="Record a new procurement opportunity into the pipeline to track intake, qualification, and responses."
  >
    <OpportunityForm key={props.initialData?.id || "new-opp"} {...props} />
  </Modal>
);
